import { useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { INVOICE_TYPES, INVOICES_RECONCILED_STATUS } from 'Utils/constants';
import { useTranslation } from 'react-i18next';
import { differenceInDays } from 'date-fns';

const { NOT_RECONCILED_LATE, NOT_RECONCILED, RECONCILED } = INVOICES_RECONCILED_STATUS;

const STATUS = {
    [NOT_RECONCILED_LATE]: {
        i18n: 'status-late',
        className: 'text-white bg-[#d15554]',
        icon: <ExpiredIcon />,
    },
    [NOT_RECONCILED]: {
        i18n: 'status-pending',
        className: 'text-[#ff5f43] bg-[rgba(255,95,66,0.1)]',
        icon: <PendingIcon />,
    },
    [RECONCILED]: {
        i18nIncome: 'status-collected',
        i18nExpense: 'status-paid',
        className: 'text-income bg-[rgba(70,155,74,0.1)]',
        icon: <PaidIcon />,
    },
};

export default function InvoiceStatusBadge(props) {
    const { status, expirationDate, className, type } = props;

    const { t } = useTranslation('invoices');
    const isIncome = type === INVOICE_TYPES.SALE;

    if (!STATUS[status]) {
        return null;
    }

    const showDayLate = status === NOT_RECONCILED_LATE && expirationDate;
    const daysLate = showDayLate ? differenceInDays(new Date(), expirationDate) : 0;

    const statusLabel = useMemo(() => {
        const statusConfig = STATUS[status];
        if (status === RECONCILED) {
            return t(isIncome ? statusConfig.i18nIncome : statusConfig.i18nExpense);
        }
        return t(statusConfig.i18n, { days: daysLate });
    }, [status, isIncome, daysLate, t]);

    return (
        <div
            className={clsx(
                'text-xs font-medium tracking-normal px-3 py-1 rounded-full leading-[1.25] relative inline-flex items-center gap-1.5 max-w-full',
                STATUS[status].className,
                className,
            )}
            title={statusLabel}
        >
            {STATUS[status].icon}
            <span className="truncate min-w-0">{statusLabel}</span>
        </div>
    );
}

InvoiceStatusBadge.propTypes = {
    status: PropTypes.string,
    expirationDate: PropTypes.instanceOf(Date),
    className: PropTypes.string,
    type: PropTypes.string,
};

function PendingIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="9" height="13" viewBox="0 0 9 13">
            <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                <g fill="currentColor" transform="translate(-156 -80)">
                    <g transform="translate(157.147 81)">
                        <path
                            fillRule="nonzero"
                            d="M7-.75H0A.75.75 0 00-.696.279l.267.66C.931 4.232 1.907 5.75 3 5.75h1c1.164 0 2.195-1.72 3.696-5.471A.75.75 0 007-.75zM1.12.75h4.759l-.114.269c-.431.996-.818 1.779-1.158 2.345-.367.612-.641.886-.607.886H3c.034 0-.24-.274-.607-.886l-.103-.176a20.709 20.709 0 01-.911-1.84L1.12.75z"
                        ></path>
                        <path d="M1 9H6V11H1z"></path>
                        <path d="M1 3L6 3 5 5 2 5z"></path>
                        <path
                            fillRule="nonzero"
                            d="M7 5.25H0a.75.75 0 00-.696 1.029l.267.66C.931 10.232 1.907 11.75 3 11.75h1c1.164 0 2.195-1.72 3.696-5.471A.75.75 0 007 5.25zm-5.88 1.5h4.759l-.114.269c-.431.996-.818 1.779-1.158 2.345-.367.612-.641.886-.607.886H3c.034 0-.24-.274-.607-.886l-.103-.176a20.709 20.709 0 01-.911-1.84L1.12 6.75z"
                            transform="matrix(1 0 0 -1 0 17)"
                        ></path>
                    </g>
                </g>
            </g>
        </svg>
    );
}

function ExpiredIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="9" height="13" viewBox="0 0 9 13">
            <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                <g fill="currentColor" transform="translate(-157 -146)">
                    <g transform="translate(158 147)">
                        <path
                            fillRule="nonzero"
                            d="M7-.75H0A.75.75 0 00-.696.279l.267.66C.931 4.232 1.907 5.75 3 5.75h1c1.164 0 2.195-1.72 3.696-5.471A.75.75 0 007-.75zM1.12.75h4.759l-.114.269c-.431.996-.818 1.779-1.158 2.345-.367.612-.641.886-.607.886H3c.034 0-.24-.274-.607-.886l-.103-.176a20.709 20.709 0 01-.911-1.84L1.12.75z"
                        ></path>
                        <path d="M2 6L5 6 6 11 1 11z"></path>
                        <path
                            fillRule="nonzero"
                            d="M7 5.25H0a.75.75 0 00-.696 1.029l.267.66C.931 10.232 1.907 11.75 3 11.75h1c1.164 0 2.195-1.72 3.696-5.471A.75.75 0 007 5.25zm-5.88 1.5h4.759l-.114.269c-.431.996-.818 1.779-1.158 2.345-.367.612-.641.886-.607.886H3c.034 0-.24-.274-.607-.886l-.103-.176a20.709 20.709 0 01-.911-1.84L1.12 6.75z"
                            transform="matrix(1 0 0 -1 0 17)"
                        ></path>
                    </g>
                </g>
            </g>
        </svg>
    );
}

function PaidIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="8" viewBox="0 0 11 8">
            <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                <g fill="currentColor" fillRule="nonzero" transform="translate(-155 -115)">
                    <g transform="translate(156 116)">
                        <path
                            d="M6.793-2.207A1 1 0 018.207-.793L2.915 4.5l2.292 2.293a1 1 0 01.083 1.32l-.083.094a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414l6-6z"
                            transform="matrix(0 -1 -1 0 7.5 7.5)"
                        ></path>
                    </g>
                </g>
            </g>
        </svg>
    );
}
